/* global BigInt */
import React from 'react';
import {Button, DatePicker, Divider, Input, message, Modal, Table} from 'antd';
import WebServerRestClient from "../../tools/WebServerRestClient";
import UserComponent from "../../components/UserComponent";
import CommonUtils from "../../tools/CommonUtils";

const { RangePicker } = DatePicker;

export default class UserTrafficStats extends UserComponent {

    constructor(props) {
        super(props);
        this.state = {
            loading: false,
            dataList: [],
            totalCount: 0,
            totalBytes: '0',
            totalUploadBytes: '0',
            totalDownloadBytes: '0',
            currentPage: 1,
            pageSize: 10,
            query_params: {},
            showHeavyUsageUsersModal: false,
            heavyUsageUsersLoading: false,
            heavyUsageLogoutLoading: false,
            heavyUsageUsers: [],
            heavyUsageSelectedRowKeys: [],
            heavyUsageTotal: 0,
            heavyUsageWindowDays: 0,
            heavyUsageSnapshotDateStart: '',
            heavyUsageSnapshotDateEnd: ''
        }
    }

    componentDidMount() {
        this.loadListData();
    }

    handleQueryInputChange(event) {
        const target = event.target;
        let query_params = Object.assign({}, this.state.query_params);
        query_params[target.name] = target.value ? target.value.trim() : '';
        this.setState({query_params: query_params});
    }

    handleDateRangeChange(dates, dateStrings) {
        let query_params = Object.assign({}, this.state.query_params);
        query_params.snapshotDateStart = dateStrings[0];
        query_params.snapshotDateEnd = dateStrings[1];
        this.setState({query_params: query_params});
    }

    loadListData(changedParams) {
        if (!changedParams || !changedParams.currentPage || !changedParams.pageSize) {
            changedParams = {
                currentPage: 1,
                pageSize: this.state.pageSize
            };
        }

        let params = Object.assign({}, this.state.query_params);
        if (!params.userId) {
            delete params.userId;
        }
        if (!params.snapshotDateStart || !params.snapshotDateEnd) {
            delete params.snapshotDateStart;
            delete params.snapshotDateEnd;
        }

        params.currentPage = changedParams.currentPage;
        params.pageSize = changedParams.pageSize;

        this.setState({loading: true});
        new WebServerRestClient().GET('/admin/user-traffic-usage', params)
            .then(response => {
                this.setState({loading: false});
                if (response.code === 200) {
                    let data = response.data || {};
                    this.setState({
                        dataList: data.rows || [],
                        totalCount: data.total || 0,
                        totalBytes: data.totalBytes || '0',
                        totalUploadBytes: data.totalUploadBytes || '0',
                        totalDownloadBytes: data.totalDownloadBytes || '0',
                        currentPage: data.currentPage || changedParams.currentPage,
                        pageSize: data.pageSize || changedParams.pageSize
                    });
                } else if (response.code === 401 || response.code === 403) {
                    this.props.history.push('/login');
                } else {
                    message.error(response['msg']);
                }
            }).catch(err => {
                this.setState({loading: false});
                message.error("Error: " + err.message);
            });
    }

    loadHeavyUsageUsers() {
        this.setState({
            showHeavyUsageUsersModal: true,
            heavyUsageUsersLoading: true,
            heavyUsageSelectedRowKeys: []
        });
        new WebServerRestClient().GET('/admin/node-traffic/heavy-usage-users')
            .then(response => {
                this.setState({heavyUsageUsersLoading: false});
                if (response.code === 200) {
                    let data = response.data || {};
                    let rows = Array.isArray(data) ? data : (data.rows || []);
                    this.setState({
                        heavyUsageUsers: rows,
                        heavyUsageTotal: data.total || rows.length,
                        heavyUsageWindowDays: data.windowDays || 0,
                        heavyUsageSnapshotDateStart: data.snapshotDateStart || '',
                        heavyUsageSnapshotDateEnd: data.snapshotDateEnd || ''
                    });
                } else if (response.code === 401 || response.code === 403) {
                    this.props.history.push('/login');
                } else {
                    message.error(response['msg']);
                }
            }).catch(err => {
                this.setState({heavyUsageUsersLoading: false});
                message.error("Error: " + err.message);
            });
    }

    buildHeavyUsageLogoutRoute(userId) {
        let route = '/admin/node-traffic/heavy-usage-users/devices/logout';
        if (userId) {
            route += '?userId=' + encodeURIComponent(userId);
        }
        return route;
    }

    requestHeavyUsageLogout(userId) {
        return new WebServerRestClient().POST(this.buildHeavyUsageLogoutRoute(userId))
            .then(response => {
                if (response.code === 200) {
                    return response.data || {};
                }
                if (response.code === 401 || response.code === 403) {
                    this.props.history.push('/login');
                    throw new Error('Please Log In!');
                }
                throw new Error(response['msg'] || 'Logout failed');
            });
    }

    logoutHeavyUsageUsersByIds(userIds) {
        return userIds.reduce((promise, userId) => {
            return promise.then(result => {
                return this.requestHeavyUsageLogout(userId).then(data => {
                    let targetUserIds = data.targetUserIds || [];
                    return {
                        updatedDeviceCount: result.updatedDeviceCount + (data.updatedDeviceCount || 0),
                        targetUserIds: result.targetUserIds.concat(targetUserIds.length > 0 ? targetUserIds : [userId])
                    };
                });
            });
        }, Promise.resolve({updatedDeviceCount: 0, targetUserIds: []}));
    }

    confirmLogoutHeavyUsageUsers() {
        let selectedRowKeys = this.state.heavyUsageSelectedRowKeys || [];
        if (selectedRowKeys.length === 0) {
            message.warn('Please select users first.');
            return;
        }

        Modal.confirm({
            title: 'Logout selected heavy usage users?',
            content: `This will force logout devices for ${selectedRowKeys.length} selected user(s).`,
            okText: 'Logout',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: () => this.logoutSelectedHeavyUsageUsers()
        });
    }

    logoutSelectedHeavyUsageUsers() {
        let selectedRowKeys = this.state.heavyUsageSelectedRowKeys || [];
        let allUserIds = (this.state.heavyUsageUsers || []).map(row => `${row.userId}`);
        let selectedAll = allUserIds.length > 0 && selectedRowKeys.length === allUserIds.length;

        this.setState({heavyUsageLogoutLoading: true});
        let logoutPromise = selectedAll
            ? this.requestHeavyUsageLogout()
            : this.logoutHeavyUsageUsersByIds(selectedRowKeys);

        return logoutPromise.then(data => {
            let targetUserIds = data.targetUserIds || [];
            this.setState({
                heavyUsageLogoutLoading: false,
                heavyUsageSelectedRowKeys: []
            });
            message.success(
                `Logout completed: ${targetUserIds.length || selectedRowKeys.length} user(s), ` +
                `${data.updatedDeviceCount || 0} device(s) updated.`
            );
            return data;
        }).catch(err => {
            this.setState({heavyUsageLogoutLoading: false});
            if (err.message !== 'Please Log In!') {
                message.error("Error: " + err.message);
            }
            throw err;
        });
    }

    formatTimestamp(timestamp) {
        if (!timestamp) {
            return '';
        }
        let time = Number(timestamp);
        if (isNaN(time)) {
            return timestamp;
        }
        if (time < 1000000000000) {
            time *= 1000;
        }
        return CommonUtils.formatDate(new Date(time), 'yyyy-MM-dd hh:mm:ss');
    }

    formatBytes(bytes) {
        let value = this.parseBytes(bytes);
        if (value <= BigInt(0)) {
            return '0 KB';
        }
        let base = BigInt(1024);
        let hundred = BigInt(100);
        let units = ['KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
        let divisor = base;
        let index = 0;
        while (value >= divisor * base && index < units.length - 1) {
            divisor = divisor * base;
            index++;
        }

        let scaledValue = (value * hundred + divisor / BigInt(2)) / divisor;
        if (scaledValue === BigInt(0)) {
            scaledValue = BigInt(1);
        }
        let integerPart = scaledValue / hundred;
        let decimalPart = (scaledValue % hundred).toString();
        while (decimalPart.length < 2) {
            decimalPart = '0' + decimalPart;
        }
        return integerPart.toString() + '.' + decimalPart + ' ' + units[index];
    }

    normalizeBytes(bytes) {
        if (typeof bytes === 'undefined' || bytes === null) {
            return '0';
        }
        let value = `${bytes}`.trim();
        if (!/^\d+$/.test(value)) {
            return '0';
        }
        value = value.replace(/^0+/, '');
        return value || '0';
    }

    parseBytes(bytes) {
        return BigInt(this.normalizeBytes(bytes));
    }

    compareBytes(left, right) {
        let leftBytes = this.parseBytes(left);
        let rightBytes = this.parseBytes(right);
        if (leftBytes === rightBytes) {
            return 0;
        }
        return leftBytes > rightBytes ? 1 : -1;
    }

    renderHeavyUsageUsersModal() {
        const selectedRowKeys = this.state.heavyUsageSelectedRowKeys || [];
        const rowSelection = {
            selectedRowKeys: selectedRowKeys,
            onChange: (selectedRowKeys) => {
                this.setState({heavyUsageSelectedRowKeys: selectedRowKeys});
            }
        };

        return (
            <Modal
                title="Heavy Usage Users"
                width={1200}
                visible={this.state.showHeavyUsageUsersModal}
                maskClosable={false}
                onCancel={() => this.setState({showHeavyUsageUsersModal: false})}
                footer={[
                    <Button key="refresh"
                            icon="reload"
                            loading={this.state.heavyUsageUsersLoading}
                            onClick={this.loadHeavyUsageUsers.bind(this)}>
                        Refresh
                    </Button>,
                    <Button key="logout"
                            type="danger"
                            icon="logout"
                            loading={this.state.heavyUsageLogoutLoading}
                            disabled={selectedRowKeys.length === 0}
                            onClick={this.confirmLogoutHeavyUsageUsers.bind(this)}>
                        Logout
                    </Button>
                ]}>
                {this.renderHeavyUsageSummary()}
                <Table loading={this.state.heavyUsageUsersLoading}
                       rowKey={(record) => `${record.userId}`}
                       rowSelection={rowSelection}
                       size="small"
                       pagination={false}
                       scroll={{x: 960, y: 520}}
                       expandedRowRender={(record) => this.renderDailyUsageTable(record)}
                       columns={[
                           {title: 'UserId', dataIndex: 'userId', align: 'center', width: 110},
                           {title: 'Email', dataIndex: 'email', align: 'left', width: 260},
                           {
                               title: 'Window Total',
                               dataIndex: 'totalBytes',
                               align: 'right',
                               width: 140,
                               sorter: (a, b) => this.compareBytes(a.totalBytes, b.totalBytes),
                               render: (value) => this.formatBytes(value)
                           },
                           {
                               title: 'Upload',
                               dataIndex: 'totalUploadBytes',
                               align: 'right',
                               width: 120,
                               render: (value) => this.formatBytes(value)
                           },
                           {
                               title: 'Download',
                               dataIndex: 'totalDownloadBytes',
                               align: 'right',
                               width: 120,
                               render: (value) => this.formatBytes(value)
                           },
                           {
                               title: 'Daily Avg',
                               dataIndex: 'averageDailyBytes',
                               align: 'right',
                               width: 120,
                               sorter: (a, b) => this.compareBytes(a.averageDailyBytes, b.averageDailyBytes),
                               render: (value) => this.formatBytes(value)
                           }
                       ]}
                       dataSource={this.state.heavyUsageUsers}/>
            </Modal>
        );
    }

    renderHeavyUsageSummary() {
        if (!this.state.heavyUsageSnapshotDateStart || !this.state.heavyUsageSnapshotDateEnd) {
            return null;
        }

        return (
            <div style={{
                marginBottom: 12,
                lineHeight: '24px',
                color: '#606266'
            }}>
                <span>
                    Period <b style={{color: '#303133'}}>{this.state.heavyUsageSnapshotDateStart}</b>
                    {' to '}
                    <b style={{color: '#303133'}}>{this.state.heavyUsageSnapshotDateEnd}</b>
                </span>
                <Divider type="vertical"/>
                <span>
                    Window <b style={{color: '#303133'}}>{this.state.heavyUsageWindowDays}</b> days
                </span>
                <Divider type="vertical"/>
                <span>
                    Users <b style={{color: '#303133'}}>{this.state.heavyUsageTotal}</b>
                </span>
            </div>
        );
    }

    renderDailyUsageTable(record) {
        return (
            <Table rowKey={(item) => `${record.userId}_${item.snapshotDate}`}
                   size="small"
                   pagination={false}
                   dataSource={record.dailyUsage || []}
                   columns={[
                       {title: 'SnapshotDate', dataIndex: 'snapshotDate', align: 'center', width: 160},
                       {
                           title: 'Upload',
                           dataIndex: 'uploadBytes',
                           align: 'right',
                           render: (value) => this.formatBytes(value)
                       },
                       {
                           title: 'Download',
                           dataIndex: 'downloadBytes',
                           align: 'right',
                           render: (value) => this.formatBytes(value)
                       },
                       {
                           title: 'Total',
                           dataIndex: 'totalBytes',
                           align: 'right',
                           render: (value) => this.formatBytes(value)
                       }
                   ]}/>
        );
    }

    render() {
        return (
            <div className="container">
                <div className="toolbar">
                    <Input placeholder="User ID"
                           name="userId"
                           className="search_input_middle"
                           onChange={this.handleQueryInputChange.bind(this)}/>
                    <Divider type="vertical"/>
                    <RangePicker format="YYYY-MM-DD"
                                 style={{width: 260}}
                                 onChange={this.handleDateRangeChange.bind(this)}/>
                    <Divider type="vertical"/>
                    <Button type="primary" icon="search" className="search_btn" loading={this.state.loading}
                            onClick={() => this.loadListData()}>Search</Button>
                    <div className="button_group">
                        <Button type="link"
                                icon="bar-chart"
                                size={"small"}
                                loading={this.state.heavyUsageUsersLoading}
                                onClick={this.loadHeavyUsageUsers.bind(this)}>
                            Heavy Usage Users
                        </Button>
                    </div>
                </div>

                <div className="total_bar">
                    <span className="right">
                        Upload <b style={{color:'red'}}>{this.formatBytes(this.state.totalUploadBytes)}</b>
                        <Divider type="vertical"/>
                        Download <b style={{color:'red'}}>{this.formatBytes(this.state.totalDownloadBytes)}</b>
                        <Divider type="vertical"/>
                        Total <b style={{color:'red'}}>{this.formatBytes(this.state.totalBytes)}</b>
                    </span>
                </div>

                <Table loading={this.state.loading}
                       rowKey={(record) => record.id || `${record.userId}_${record.snapshotDate}`}
                       columns={[
                           {title: 'UserId', dataIndex: 'userId', align: 'center'},
                           {title: 'SnapshotDate', dataIndex: 'snapshotDate', align: 'center'},
                           {
                               title: 'Upload',
                               dataIndex: 'uploadBytes',
                               align: 'right',
                               render: (value) => this.formatBytes(value)
                           },
                           {
                               title: 'Download',
                               dataIndex: 'downloadBytes',
                               align: 'right',
                               render: (value) => this.formatBytes(value)
                           },
                           {
                               title: 'Total',
                               dataIndex: 'totalBytes',
                               align: 'right',
                               render: (value) => this.formatBytes(value)
                           },
                           {
                               title: 'CreatedTime',
                               dataIndex: 'createdTime',
                               align: 'center',
                               render: (value) => this.formatTimestamp(value)
                           },
                           {
                               title: 'UpdatedTime',
                               dataIndex: 'updatedTime',
                               align: 'center',
                               render: (value) => this.formatTimestamp(value)
                           }
                       ]}
                       dataSource={this.state.dataList}
                       pagination={{
                           current: this.state.currentPage,
                           pageSize: this.state.pageSize,
                           total: this.state.totalCount,
                           onChange: (currentPage, pageSize) => {
                               this.setState({loading: true});
                               this.loadListData({currentPage, pageSize});
                           },
                           showTotal: ((total) => {
                               return `total of ${total} records`;
                           }),
                           className: "pagination"
                       }}/>
                {this.renderHeavyUsageUsersModal()}
            </div>
        )
    }

}
